import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import type { Group, Mesh } from "three";

interface TreeFallProps {
  position: [number, number, number];
  hue?: number;
  /** Called once the fall animation completes (~2.5s). */
  onComplete?: () => void;
}

/**
 * Animated tree fall: shudders, tilts and topples over, then foliage
 * detaches and scatters while the trunk fades. ~2.5 seconds via useFrame.
 */
export function TreeFall({
  position,
  hue = 110,
  onComplete,
}: TreeFallProps) {
  const groupRef = useRef<Group>(null);
  const trunkRef = useRef<Group>(null);
  const canopyRef = useRef<Group>(null);
  const startTime = useRef<number | null>(null);
  const completed = useRef(false);

  const TRUNK_HEIGHT = 3.2;
  const TRUNK_RADIUS = 0.12;
  const canopyColor = `hsl(${hue}, 82%, 55%)`;
  const trunkColor = "#6b5a45";

  const foliageClusters = useMemo(() => {
    const clusters: { x: number; y: number; z: number; r: number }[] = [];
    const crownY = TRUNK_HEIGHT * 0.78;
    const baseR = 1.0;

    clusters.push({ x: 0, y: crownY + baseR * 0.35, z: 0, r: baseR });
    clusters.push({ x: 0, y: crownY + baseR * 0.95, z: 0, r: baseR * 0.55 });

    const count = 7;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const spread = baseR * 0.7;
      const yOff = ((i * 13) % 7 - 3) * 0.1;
      const sz = baseR * (0.35 + ((i * 11) % 5) * 0.07);
      clusters.push({
        x: Math.cos(a) * spread,
        y: crownY + yOff,
        z: Math.sin(a) * spread,
        r: sz,
      });
    }

    const lowerCount = 4;
    for (let i = 0; i < lowerCount; i++) {
      const a = (i / lowerCount) * Math.PI * 2 + 0.3;
      const spread = baseR * 0.85 + 0.25;
      clusters.push({
        x: Math.cos(a) * spread,
        y: crownY - 0.35 - ((i * 17) % 4) * 0.12,
        z: Math.sin(a) * spread,
        r: baseR * 0.3,
      });
    }
    return clusters;
  }, []);

  // Random scatter directions for each foliage cluster
  const scatterDirs = useMemo(() => {
    return foliageClusters.map((_, i) => {
      const a = (i / foliageClusters.length) * Math.PI * 2 + 0.5;
      return {
        dx: Math.cos(a) * (1.5 + Math.random()),
        dy: 0.5 + Math.random() * 0.8,
        dz: Math.sin(a) * (1.5 + Math.random()),
      };
    });
  }, [foliageClusters]);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;

    const group = groupRef.current;
    const trunk = trunkRef.current;
    const canopy = canopyRef.current;
    if (!group || !trunk || !canopy) return;

    if (t < 0.3) {
      // Phase 1: shudder
      const shake = Math.sin(t * 80) * 0.03 * (t / 0.3);
      group.rotation.z = shake;
      trunk.scale.set(1, 1, 1);
      trunk.position.y = TRUNK_HEIGHT / 2;
    } else if (t < 1.5) {
      // Phase 2: topple
      const p = (t - 0.3) / 1.2;
      const eased = p * p;
      group.rotation.z = eased * (Math.PI * 0.44);
      trunk.scale.set(1, 1, 1);
      trunk.position.y = TRUNK_HEIGHT / 2;
      // Foliage still attached, scaling normally
      const children = canopy.children;
      for (let i = 0; i < children.length; i++) {
        children[i].scale.setScalar(1);
      }
    } else if (t < 2.5) {
      // Phase 3: foliage detaches, scatters, fades; trunk fades
      const p = (t - 1.5) / 1.0;
      group.rotation.z = Math.PI * 0.44;

      // Trunk fades
      const trunkMesh = trunk.children[0] as Mesh | undefined;
      if (trunkMesh?.material) {
        (trunkMesh.material as any).opacity = Math.max(0, 1 - p);
        (trunkMesh.material as any).transparent = true;
      }

      // Foliage scatters
      const children = canopy.children;
      for (let i = 0; i < children.length; i++) {
        const dir = scatterDirs[i];
        if (!dir) continue;
        const child = children[i];
        const stagger = Math.min(1, p * 1.5 - (i / children.length) * 0.3);
        const sp = Math.max(0, stagger);
        child.position.x = foliageClusters[i].x + dir.dx * sp;
        child.position.y = foliageClusters[i].y + dir.dy * sp - sp * sp * 2;
        child.position.z = foliageClusters[i].z + dir.dz * sp;
        child.scale.setScalar(Math.max(0, 1 - sp));
        if ((child as Mesh).material) {
          ((child as Mesh).material as any).opacity = Math.max(0, 1 - sp);
          ((child as Mesh).material as any).transparent = true;
        }
      }
    } else {
      // Done
      group.visible = false;
      if (!completed.current) {
        completed.current = true;
        onComplete?.();
      }
    }
  });

  return (
    <group ref={groupRef} position={position}>
      <group ref={trunkRef} position={[0, TRUNK_HEIGHT / 2, 0]}>
        <mesh>
          <cylinderGeometry
            args={[TRUNK_RADIUS * 0.65, TRUNK_RADIUS, TRUNK_HEIGHT, 8]}
          />
          <meshStandardMaterial color={trunkColor} roughness={0.9} transparent />
        </mesh>
      </group>

      <group ref={canopyRef}>
        {foliageClusters.map((c, i) => (
          <mesh key={i} position={[c.x, c.y, c.z]}>
            <dodecahedronGeometry args={[c.r, 1]} />
            <meshStandardMaterial color={canopyColor} roughness={0.7} transparent />
          </mesh>
        ))}
      </group>
    </group>
  );
}
